import { Loader2, MessageCircle, CheckCircle2, Search } from "lucide-react";
import { Link } from "react-router-dom";
import { Reveal } from "@/components/launchx/Reveal";
import { agencyWa } from "./AgencyLayout";
import { useAgencyLead, SERVICE_OPTIONS, INDUSTRIES } from "./useAgencyLead";

const inputCls = "w-full rounded-xl border border-white/15 bg-navy px-4 py-3.5 text-sm text-white placeholder:text-slate-500 outline-none focus:border-electric transition-colors";
const labelCls = "block text-xs font-bold uppercase tracking-[0.15em] text-slate-400 mb-2";

export const AgencyAuditForm = () => {
  const { form, set, toggleService, busy, done, submit } = useAgencyLead("audit");

  return (
    <section id="audit" data-testid="agency-audit" className="bg-background py-20 sm:py-28">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-14 items-start">
        <Reveal>
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-blue-600">Free Digital Audit</p>
          <h2 className="mt-4 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900">
            Find out exactly why customers aren't finding you online
          </h2>
          <p className="mt-4 text-base text-slate-600 leading-relaxed max-w-xl">
            In a free 30-minute audit we review your Google presence, social profiles and competitors — and hand you a clear action plan, whether you work with us or not.
          </p>
          <ul className="mt-8 space-y-4">
            {[
              "Google Search & Maps visibility check",
              "Website and WhatsApp enquiry review",
              "Competitor comparison in your area",
              "Written action plan with honest priorities",
            ].map((t) => (
              <li key={t} className="flex items-start gap-3 text-sm text-slate-700">
                <CheckCircle2 size={17} className="mt-0.5 shrink-0 text-electric" /> {t}
              </li>
            ))}
          </ul>
          <div className="mt-9 bg-navy rounded-3xl p-7">
            <p className="text-sm font-semibold text-white">Prefer to just talk?</p>
            <p className="mt-1.5 text-sm text-slate-400">Message us on WhatsApp — tell us about your business and we'll take it from there.</p>
            <a data-testid="agency-audit-whatsapp-button" href={agencyWa("I want a free digital audit for my business.")} target="_blank" rel="noreferrer"
              className="mt-4 inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1fb857] text-white text-sm font-semibold rounded-full px-6 py-3.5 transition-colors">
              <MessageCircle size={16} /> WhatsApp for Free Audit
            </a>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="bg-navy rounded-3xl p-7 sm:p-9 border border-white/10" data-testid="agency-audit-form-card">
            {done ? (
              <div className="text-center py-10" data-testid="agency-audit-success">
                <CheckCircle2 size={52} className="mx-auto text-cyanx" />
                <h3 className="mt-5 font-display text-xl font-bold text-white">Audit request received!</h3>
                <p className="mt-2 text-sm text-slate-400 max-w-sm mx-auto">
                  Your reference is <span className="font-bold text-cyanx">{done.lead_id}</span>. Our team will contact you within one working day.
                </p>
                <a data-testid="agency-audit-success-whatsapp" href={agencyWa(`Hi, I just submitted an audit request (${done.lead_id}).`)} target="_blank" rel="noreferrer"
                  className="mt-6 inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1fb857] text-white text-sm font-semibold rounded-full px-6 py-3.5 transition-colors">
                  <MessageCircle size={15} /> Continue on WhatsApp
                </a>
              </div>
            ) : (
              <form data-testid="agency-audit-form" onSubmit={(e) => { e.preventDefault(); submit(); }} className="space-y-5">
                <div className="flex items-center gap-2.5">
                  <Search size={18} className="text-cyanx" />
                  <h3 className="font-display text-lg font-bold text-white">Request Your Free Audit</h3>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className={labelCls} htmlFor="audit-name">Your Name *</label>
                    <input id="audit-name" data-testid="audit-name-input" required className={inputCls} placeholder="Full name" value={form.name} onChange={(e) => set("name", e.target.value)} />
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="audit-mobile">Mobile (WhatsApp) *</label>
                    <input id="audit-mobile" data-testid="audit-mobile-input" required inputMode="numeric" maxLength={10} className={inputCls} placeholder="10-digit mobile" value={form.mobile} onChange={(e) => set("mobile", e.target.value.replace(/\D/g, "").slice(0, 10))} />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className={labelCls} htmlFor="audit-business">Business Name</label>
                    <input id="audit-business" data-testid="audit-business-input" className={inputCls} placeholder="Your business name" value={form.business_name} onChange={(e) => set("business_name", e.target.value)} />
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="audit-industry">Industry</label>
                    <select id="audit-industry" data-testid="audit-industry-select" className={inputCls} value={form.industry} onChange={(e) => set("industry", e.target.value)}>
                      <option value="">Select industry</option>
                      {INDUSTRIES.map((i) => <option key={i} value={i}>{i}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <label className={labelCls} htmlFor="audit-website">Current Website / Social Link (if any)</label>
                  <input id="audit-website" data-testid="audit-website-input" className={inputCls} placeholder="https:// or Instagram/Facebook link" value={form.website_url} onChange={(e) => set("website_url", e.target.value)} />
                </div>
                <div>
                  <label className={labelCls}>What do you need help with?</label>
                  <div className="flex flex-wrap gap-2" data-testid="audit-services-group">
                    {SERVICE_OPTIONS.map((s) => (
                      <button type="button" key={s} data-testid={`audit-service-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} onClick={() => toggleService(s)}
                        className={`text-xs font-semibold rounded-full px-4 py-2 border transition-colors ${form.services_interested.includes(s) ? "bg-electric border-electric text-white" : "border-white/15 text-slate-300 hover:border-cyanx/50"}`}>
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className={labelCls} htmlFor="audit-challenge">Biggest challenge right now</label>
                  <textarea id="audit-challenge" data-testid="audit-challenge-input" rows={3} className={inputCls} placeholder="e.g. Customers can't find us on Google / competitors are running ads" value={form.challenge} onChange={(e) => set("challenge", e.target.value)} />
                </div>
                <label className="flex items-start gap-3 cursor-pointer" data-testid="audit-consent-label">
                  <input type="checkbox" data-testid="audit-consent-checkbox" checked={form.consent} onChange={(e) => set("consent", e.target.checked)} className="mt-1 w-4 h-4 accent-electric" />
                  <span className="text-xs text-slate-400 leading-relaxed">
                    I agree to be contacted by LaunchX Agency about my enquiry and accept the{" "}
                    <Link to="/privacy-policy" className="text-cyanx underline" target="_blank">Privacy Policy</Link> and{" "}
                    <Link to="/terms-and-conditions" className="text-cyanx underline" target="_blank">Terms</Link>.
                  </span>
                </label>
                <input type="text" value={form.website} onChange={(e) => set("website", e.target.value)} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
                <button type="submit" data-testid="audit-submit-button" disabled={busy}
                  className="w-full inline-flex items-center justify-center gap-2 bg-electric hover:bg-electric-dark disabled:opacity-60 text-white font-semibold text-sm rounded-xl px-7 py-4 transition-all hover:shadow-lg hover:shadow-electric/40">
                  {busy && <Loader2 size={15} className="animate-spin" />} Request Free Audit
                </button>
              </form>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
};
